import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { JsonObjectFactoryService } from '../../../global/services/json-object-factory.service';

/**
 * Describes one row returned by `loadJsonObject?action_code=siao78`.
 * Shape is dictated by the backend — do not rename fields.
 */
export interface ReturnReviewStep {
  STEP_NAME: string;
  STATE_URL: string; // legacy ui-router state name, e.g. `submissions-dasboard.submission.return-review.efile-diagnostics`
  STEP_STATUS: 'PENDING' | 'RESOLVED' | 'IGNORED' | 'FAILED' | string;
  WORKFLOW_STEP_ID: number | string;
  EXPLANATION?: string;
  IGNORED_BY?: string;
  USERNAME?: string;
  /** Last workflow status change from `siao78` (string or Oracle bytes object). */
  UPDATED_ON?: string | { length?: number; bytes?: string; null?: boolean };
  /** Optional fields returned by `siao78` / export SP for dashboard rows. */
  ERROR_STATUS?: string;
  ERROR_STATUS_LABEL?: string;
  HAS_ERRORS?: string | boolean;
  COMMENTS?: string;
  COMMENT?: string;
  REVIEWER_COMMENTS?: string;
  REVIEWED_TS?: string;
  REVIEWED_TIMESTAMP?: string;
  REVIEWED_ON?: string;
  REVIEW_ON?: string;
  IGNORED_ON?: string;
  RESOLVED_ON?: string;
  STATUS_UPDATED_ON?: string;
  STEP_REVIEWED_ON?: string;
  LAST_REVIEWED?: string;
  LAST_REVIEWED_TS?: string;
  LAST_REVIEWED_ON?: string;
  UPDATED_TS?: string;
  LAST_UPDATED_ON?: string;
  MODIFIED_ON?: string;
}

export interface IgnoreStepPayload {
  sso_id: string;
  submission_id: string | number;
  step_id: string | number;
  step_status: 'IGNORED';
  explanation: string;
  tax_year: string | number;
  scenario: string | number;
  jcd_key: number;
}

/**
 * Payload for the auto-status-update POSTed every time a return-review
 * grid finishes loading data fresh from the server (legacy
 * `EfileWorkflowController.js` L330-345). `step_status` is computed by
 * the per-step `statusCheckFn` predicate (RESOLVED when no failing rows
 * remain, FAILED otherwise).
 */
export interface UpdateStepStatusPayload {
  sso_id: string;
  submission_id: string | number;
  step_id: string | number;
  step_status: 'RESOLVED' | 'FAILED';
  tax_year: string | number;
  scenario: string | number;
  jcd_key: number;
}

/**
 * Payload for per-row comment saves on the return-review grids
 * (efile-diagnostics ↔ `woz0og`, py-cy ↔ `uqttl8`). Mirrors
 * `EfileFactory.saveComment` in `EfileService.js` L712-731. The
 * step-discriminated extras (`diag_name` for diagnostics, `form` for
 * py-cy) are passed through verbatim — the SP ignores unrelated keys.
 */
export interface SaveCommentPayload {
  submission_id: string | number;
  sso_id: string;
  workflow_step_id: string | number;
  tax_year: string | number;
  scenario: string | number;
  jcd_key: number;
  comment: string;
  diag_name?: string;
  form?: string;
}

/**
 * HTTP port of {@code EfileReturnReviewController} in
 * `ui/app/components/efile/EfileWorkflowController.js`.
 *
 *  - `loadSteps`         -> `GET  /loadJsonObject?action_code=siao78`  (L107)
 *  - `ignoreStep`        -> `POST /saveJsonObject?action_code=q05d2n`  (L83)
 *  - `updateStepStatus`  -> `POST /saveJsonObject?action_code=q05d2n`  (L330)
 *  - `saveComment`       -> `POST /saveJsonObject?action_code=<woz0og|uqttl8>` (EfileService.js L712)
 *  - `loadIrsSubmissions`-> `GET  /loadJsonObject?action_code=j4k0an`  (efile.js L996)
 *
 * Callers supply `baseUrl` (the equivalent of `GENERAL_CONFIG.base_url`,
 * typically obtained via `generalConfigFromGlobal(globalService).base_url`).
 */
@Injectable({ providedIn: 'root' })
export class EfileWorkflowService {
  /**
   * WORKFLOW_STEP_ID of the Return Review tab currently on screen.
   * Inline comment saves (`savePyCyComment` / `saveEfileDiagComment`)
   * read this when gizmoGrid fires a type=`input` save that does not
   * carry the step id on the row payload (legacy
   * `efileFactory.returnReviewSteps.find(s => s.STATE_URL === $state.current.name)`).
   */
  activeWorkflowStepId: string | number | null = null;

  private static readonly ACTION_CODES = {
    loadSteps: 'siao78',
    ignoreStep: 'q05d2n',
    /** Same backend as ignoreStep — distinguished only by step_status. */
    updateStepStatus: 'q05d2n',
    /** Diagnostics row comment — efile.js L659. */
    diagComment: 'woz0og',
    /** PY/CY row comment — efile.js L896. */
    pyCyComment: 'uqttl8',
    /** IRS Submissions filter loader — efile.js L996/L1054 (xml-vs-elements*). */
    irsSubmissions: 'j4k0an',
  } as const;

  constructor(
    private http: HttpClient,
    private jsonObjectFactory: JsonObjectFactoryService,
  ) {}

  /**
   * Mirrors `EfileWorkflowController.js` L107 (`JsonObjectFactory.getJSONObj`
   * with `action_code: 'siao78'`). The legacy factory transparently merges
   * `globalParams` (jcd_key / jcd_ta_key / activity_key / screen_key /
   * screen_type / is_locked / issue_id / issue_short_desc) and the active
   * screen's `filterParams` (tax_year / scenario) into the GET query — the
   * SP returns an empty workflow step list when those keys are absent,
   * which is why a hand-rolled `{action_code, submission_id}` payload made
   * the spinner clear with zero rows. Routing through
   * `JsonObjectFactoryService.getJSONObj` here re-establishes parity with
   * the legacy payload contract.
   */
  loadSteps(
    baseUrl: string,
    submissionId: string | number,
    globalService: any,
    options?: { fresh?: boolean },
  ): Observable<{ callSuccess?: string; jsonObject?: ReturnReviewStep[] }> {
    const url = `${baseUrl}/loadJsonObject`;
    const params = {
      action_code: EfileWorkflowService.ACTION_CODES.loadSteps,
      submission_id: String(submissionId ?? ''),
    };
    return this.jsonObjectFactory.getJSONObj(url, params, globalService, undefined, {
      fresh: !!options?.fresh,
    }) as Observable<{
      callSuccess?: string;
      jsonObject?: ReturnReviewStep[];
    }>;
  }

  ignoreStep(baseUrl: string, payload: IgnoreStepPayload): Observable<any> {
    return this.postForm(baseUrl, EfileWorkflowService.ACTION_CODES.ignoreStep, payload);
  }

  /**
   * POST /saveJsonObject?action_code=q05d2n with a non-IGNORED status.
   * Mirrors the auto-status-update block in `EfileWorkflowController.js`
   * L330-344 — runs on every successful fresh grid load to keep the tab
   * strip's RESOLVED ✓ / FAILED ✗ icons in sync with the underlying
   * data. The success response triggers a `reload-steps` broadcast in
   * legacy code; our caller re-fetches steps to mirror that.
   */
  updateStepStatus(baseUrl: string, payload: UpdateStepStatusPayload): Observable<any> {
    return this.postForm(baseUrl, EfileWorkflowService.ACTION_CODES.updateStepStatus, payload);
  }

  /**
   * POST /saveJsonObject?action_code=<actionCode> for the diagnostics +
   * py-cy row comments. `kind` selects the legacy action_code; the
   * payload itself carries the kind-specific extras (`diag_name`,
   * `form`). Mirrors `EfileFactory.saveComment` (EfileService.js L712).
   */
  saveComment(
    baseUrl: string,
    kind: 'diag' | 'pycy',
    payload: SaveCommentPayload,
  ): Observable<any> {
    const actionCode =
      kind === 'diag'
        ? EfileWorkflowService.ACTION_CODES.diagComment
        : EfileWorkflowService.ACTION_CODES.pyCyComment;
    return this.postForm(baseUrl, actionCode, payload);
  }

  /**
   * GET /loadJsonObject?action_code=j4k0an for the "IRS Submissions"
   * dropdown that the legacy `xml-vs-elements` and `xml-vs-elements-counts`
   * states render above their grid (efile.js L996-L1029 / L1052-L1085).
   * Returns `{ jsonObject: Array<{ LABEL, VALUE }> }`.
   */
  loadIrsSubmissions(
    baseUrl: string,
    submissionId: string | number,
    globalService: any,
    options?: { fresh?: boolean },
  ): Observable<{ callSuccess?: string; jsonObject?: Array<{ LABEL: string; VALUE: string }> }> {
    const url = `${baseUrl}/loadJsonObject`;
    const params = {
      action_code: EfileWorkflowService.ACTION_CODES.irsSubmissions,
      submission_id: String(submissionId ?? ''),
    };
    // Same rationale as `loadSteps`: route through `JsonObjectFactory.getJSONObj`
    // so global + filter params (jcd_key, scenario, tax_year, etc.) are
    // merged into the GET, matching the legacy `j4k0an` call signature.
    return this.jsonObjectFactory.getJSONObj(url, params, globalService, undefined, {
      fresh: !!options?.fresh,
    }) as Observable<{
      callSuccess?: string;
      jsonObject?: Array<{ LABEL: string; VALUE: string }>;
    }>;
  }

  /**
   * POST /multiGridExportToExcel for Return Review dashboard export.
   * Mirrors `EfileFactory.exportReturnReview` in `EfileService.js` L574-590.
   */
  exportReturnReview(
    baseUrl: string,
    ssoId: string,
    jsonObj: string,
    headerArray: string,
  ): Observable<any> {
    const url = `${baseUrl}/multiGridExportToExcel?exportName=Efile_Return_Review_Export&sso_id=${encodeURIComponent(ssoId)}`;
    const body = new URLSearchParams();
    body.set('jsonObj', jsonObj);
    body.set('headerArray', headerArray);
    return this.http.post<any>(url, body.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      withCredentials: true,
    });
  }

  /**
   * GET /getAPIAuthToken — retrieves a short-lived download token.
   * Mirrors the `$http.get(api_base_url + '/getAPIAuthToken')` call in
   * `EfileWorkflowController.js` L147.
   */
  getApiAuthToken(baseUrl: string): Observable<{ apiToken?: string }> {
    return this.http.get<{ apiToken?: string }>(`${baseUrl}/getAPIAuthToken`, {
      withCredentials: true,
    });
  }

  private postForm(
    baseUrl: string,
    actionCode: string,
    payload: Record<string, any>,
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=${actionCode}`;
    const body = new URLSearchParams();
    Object.keys(payload).forEach((key) => {
      const value = payload[key];
      if (value !== null && value !== undefined) {
        body.set(key, String(value));
      }
    });
    return this.http.post<any>(url, body.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      withCredentials: true,
    });
  }
}
